import * as React from 'react';
import { useSelector } from 'react-redux';
import { RECEIPTS_BEFORE_SCAN_COA_SKIPPED_KEY_PREFIX } from 'shared/constants/transactionsTourKeys';
import { getAuthUser } from 'store/auth/selectors';
import type { Organisation } from 'store/organisation/models';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import {
  getPwaCaptureEntities,
  getPwaCaptureReadyEntities,
  type PwaCaptureReadinessOptions,
} from 'utils/pwa-nav';

/** Same preference key as Entities list pin (`Entity/index.tsx`). */
export const PINNED_ENTITY_IDS_PREF = 'pinned-entity-ids';

function readPinnedEntityIds(
  preferencesHook: ReturnType<typeof usePreferences>,
  userUuid?: string
): string[] {
  if (!userUuid) {
    return [];
  }
  const stored = preferencesHook(
    PreferenceActions.GET,
    PreferenceSections.ENTITIES,
    { name: PINNED_ENTITY_IDS_PREF },
    userUuid
  );
  return Array.isArray(stored)
    ? stored.filter((id): id is string => typeof id === 'string')
    : [];
}

export function usePwaCaptureReadyEntities(
  entities: Organisation[] | undefined
): {
  eligibleEntities: Organisation[];
  readyEntities: Organisation[];
  pinnedEntityIds: string[];
} {
  const currentUser = useSelector(getAuthUser);
  const preferencesHook = usePreferences();

  const pinnedEntityIds = React.useMemo(
    () => readPinnedEntityIds(preferencesHook, currentUser?.userUuid),
    // Re-read when user or entities change; prefs live on the user object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser?.userUuid, currentUser?.prefDetails, preferencesHook]
  );

  const isCoaSkipped = React.useCallback(
    (organisationId: string) => {
      if (!currentUser?.userUuid) {
        return false;
      }
      const key = `${RECEIPTS_BEFORE_SCAN_COA_SKIPPED_KEY_PREFIX}-${organisationId}`;
      return (
        preferencesHook(
          PreferenceActions.GET,
          PreferenceSections.TRANSACTIONS,
          { name: key },
          currentUser.userUuid
        ) === true
      );
    },
    [currentUser?.userUuid, preferencesHook]
  );

  const readinessOptions = React.useMemo<PwaCaptureReadinessOptions>(
    () => ({ isCoaSkipped }),
    [isCoaSkipped]
  );

  const eligibleEntities = React.useMemo(
    () => getPwaCaptureEntities(entities, pinnedEntityIds),
    [entities, pinnedEntityIds]
  );

  const readyEntities = React.useMemo(
    () =>
      getPwaCaptureReadyEntities(entities, readinessOptions, pinnedEntityIds),
    [entities, readinessOptions, pinnedEntityIds]
  );

  return { eligibleEntities, readyEntities, pinnedEntityIds };
}
